/**
 * Language Detection Service for Skipper Automation
 * Automatically detects user's preferred language based on location and browser settings
 */

class LanguageDetectionService {
  /**
   * Detect user's location using IP geolocation services
   */
  static async detectUserLocation() {
    try {
      // Multiple IP geolocation services for reliability
      const services = [
        {
          url: 'https://ipapi.co/json/',
          parse: (data) => ({
            country: data.country_code,
            language: this.getLanguageFromCountry(data.country_code),
            city: data.city,
            timezone: data.timezone,
            service: 'ipapi.co'
          })
        },
        {
          url: 'https://ipinfo.io/json',
          parse: (data) => ({
            country: data.country,
            language: this.getLanguageFromCountry(data.country),
            city: data.city,
            timezone: data.timezone,
            service: 'ipinfo.io'
          })
        },
        {
          url: 'https://ip-api.com/json',
          parse: (data) => ({
            country: data.countryCode,
            language: this.getLanguageFromCountry(data.countryCode),
            city: data.city,
            timezone: data.timezone,
            service: 'ip-api.com'
          })
        }
      ];
      
      for (const service of services) {
        try {
          console.log(`Trying location service: ${service.url}`);
          const response = await fetch(service.url, { timeout: 5000 });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          
          const data = await response.json();
          const locationData = service.parse(data);
          
          console.log('Location detected:', locationData);
          return locationData;
          
        } catch (err) {
          console.warn(`Location service ${service.url} failed:`, err.message);
          continue;
        }
      }
      
      throw new Error('All location services failed');
      
    } catch (error) {
      console.error('Location detection failed, using fallback:', error);
      return this.getFallbackLocation();
    }
  }
  
  /**
   * Map country codes to supported languages
   */
  static getLanguageFromCountry(countryCode) {
    const countryLanguageMap = {
      // Portuguese-speaking countries
      'BR': 'pt', // Brazil - Primary target
      'PT': 'pt', // Portugal
      'AO': 'pt', // Angola
      'MZ': 'pt', // Mozambique
      'CV': 'pt', // Cape Verde
      'GW': 'pt', // Guinea-Bissau
      'ST': 'pt', // São Tomé and Príncipe
      'TL': 'pt', // East Timor
      'MO': 'pt', // Macau
      
      // English-speaking countries (default)
      'US': 'en', // United States
      'GB': 'en', // United Kingdom
      'CA': 'en', // Canada
      'AU': 'en', // Australia
      'NZ': 'en', // New Zealand
      'IE': 'en', // Ireland
      'ZA': 'en', // South Africa
      'IN': 'en', // India
      'SG': 'en', // Singapore
      'MY': 'en', // Malaysia
      'PH': 'en', // Philippines
      'NG': 'en', // Nigeria
      'KE': 'en', // Kenya
      'GH': 'en', // Ghana
      
      'ES': 'es',
      'MX': 'es',
      'AR': 'es',
      'CO': 'es',
      'CL': 'es',
      'PE': 'es',
      'VE': 'es',
      'EC': 'es',
      'GT': 'es',
      'CU': 'es',
      'BO': 'es',
      'DO': 'es',
      'HN': 'es',
      'PY': 'es',
      'SV': 'es',
      'NI': 'es',
      'CR': 'es',
      'PA': 'es',
      'UY': 'es',
      // 'FR': 'fr', // France - Future French support
      // 'DE': 'de', // Germany - Future German support
    };
    
    const lang = countryLanguageMap[countryCode?.toUpperCase()];
    console.log(`Country ${countryCode} mapped to language: ${lang || 'en (default)'}`);
    return lang || 'en'; // Default to English if country not mapped
  }
  
  /**
   * Fallback location detection using browser language
   */
  static getFallbackLocation() {
    try {
      // Get browser language preference
      const browserLang = navigator.language || navigator.languages[0] || 'en';
      const langCode = browserLang.split('-')[0].toLowerCase();
      
      console.log(`Browser language detected: ${browserLang}, using: ${langCode}`);
      
      // Map browser language to our supported languages
      const supportedLangs = ['en', 'pt', 'es'];
      const detectedLang = supportedLangs.includes(langCode) ? langCode : 'en';
      
      return {
        country: detectedLang === 'pt' ? 'BR' : 'US',
        language: detectedLang,
        city: 'Unknown',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        service: 'browser-fallback'
      };
      
    } catch (error) {
      console.error('Browser language detection failed:', error);
      return {
        country: 'US',
        language: 'en',
        city: 'Unknown',
        timezone: 'UTC',
        service: 'hard-fallback'
      };
    }
  }
  
  /**
   * Initialize language based on user preference or location
   */
  static async initializeLanguage() {
    try {
      // Check for user's manual language selection (highest priority)
      const storedLanguage = localStorage.getItem('skipper_language') || 
                            this.getCookie('skipper_language');
      
      if (storedLanguage) {
        console.log(`Using stored language preference: ${storedLanguage}`);
        return {
          language: storedLanguage,
          source: 'user_preference',
          location: null
        };
      }
      
      // Check URL parameter (for marketing campaigns)
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get('lang');
      if (urlLang && ['en', 'pt', 'es'].includes(urlLang)) {
        console.log(`Using URL language parameter: ${urlLang}`);
        // Store for future visits
        this.saveLanguagePreference(urlLang);
        return {
          language: urlLang,
          source: 'url_parameter',
          location: null
        };
      }
      
      // Detect from user's location
      console.log('Detecting language from user location...');
      const location = await this.detectUserLocation();
      
      // Store the detected language
      this.saveLanguagePreference(location.language);
      
      return {
        language: location.language,
        source: 'geolocation',
        location: location
      };
      
    } catch (error) {
      console.error('Language initialization failed:', error);
      return {
        language: 'en',
        source: 'error_fallback',
        location: null
      };
    }
  }
  
  /**
   * Save language preference to localStorage and cookie
   */
  static saveLanguagePreference(language) {
    try {
      localStorage.setItem('skipper_language', language);
      
      // Set cookie with 1 year expiration
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = `skipper_language=${language}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
      
      // Update HTML lang attribute for SEO
      document.documentElement.lang = language;
      
      console.log(`Language preference saved: ${language}`);
    } catch (error) {
      console.error('Failed to save language preference:', error);
    }
  }
  
  /**
   * Get cookie value by name
   */
  static getCookie(name) {
    try {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        return parts.pop().split(';').shift();
      }
    } catch (error) {
      console.error('Failed to read cookie:', error);
    }
    return null;
  }
  
  /**
   * Track language change in analytics
   */
  static trackLanguageChange(oldLang, newLang, source) {
    try {
      // Google Analytics 4 tracking
      if (window.gtag) {
        window.gtag('event', 'language_change', {
          'previous_language': oldLang,
          'new_language': newLang,
          'change_source': source,
          'custom_parameter_1': 'user_engagement'
        });
      }
      
      // Custom analytics tracking
      if (window.analytics) {
        window.analytics.track('Language Changed', {
          previousLanguage: oldLang,
          newLanguage: newLang,
          source: source,
          timestamp: new Date().toISOString()
        });
      }
      
      console.log(`Language change tracked: ${oldLang} -> ${newLang} (${source})`);
    } catch (error) {
      console.error('Failed to track language change:', error);
    }
  }
  
  /**
   * Get supported languages list
   */
  static getSupportedLanguages() {
    return [
      { 
        code: 'en', 
        name: 'English', 
        nativeName: 'English',
        flag: 'US',
        region: 'United States',
        rtl: false
      },
      { 
        code: 'pt', 
        name: 'Portuguese', 
        nativeName: 'Portuguese (Brazil)',
        flag: 'BR',
        region: 'Brazil',
        rtl: false
      },
      {
        code: 'es',
        name: 'Spanish',
        nativeName: 'Spanish',
        flag: 'ES',
        region: 'Spain / Latin America',
        rtl: false
      }
    ];
  }
  
  /**
   * Check if language is supported
   */
  static isLanguageSupported(lang) {
    const supportedCodes = this.getSupportedLanguages().map(l => l.code);
    return supportedCodes.includes(lang);
  }
}

export default LanguageDetectionService;
